import { create } from 'zustand';
import { ApiError, clearToken, getToken, request, setToken } from '@/lib/api.js';
import type { AdminLoginResponse, AdminProfile } from '@/lib/types.js';

type Status = 'loading' | 'unauthenticated' | 'authenticated';

interface AuthState {
  status: Status;
  admin: AdminProfile | null;
  error: string | null;

  hydrate: () => Promise<void>;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'loading',
  admin: null,
  error: null,

  async hydrate() {
    const token = getToken();
    if (token === null) {
      set({ status: 'unauthenticated', admin: null });
      return;
    }
    try {
      const me = await request<{ ok: true; admin: AdminProfile }>('/admin/auth/me');
      set({ status: 'authenticated', admin: me.admin });
    } catch (err) {
      // Token expired or invalid → fall back to login.
      clearToken();
      set({ status: 'unauthenticated', admin: null });
      if (err instanceof ApiError && err.status !== 401) {
        console.warn('hydrate failed:', err);
      }
    }
  },

  async login(username: string, password: string) {
    set({ error: null });
    const result = await request<AdminLoginResponse>('/admin/auth/login', {
      method: 'POST',
      body: { username, password },
      skipAuth: true,
    });
    setToken(result.access_token);
    set({ status: 'authenticated', admin: result.admin });
  },

  logout() {
    clearToken();
    set({ status: 'unauthenticated', admin: null });
  },
}));
